import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import {
    CalendarDays,
    Clock,
    User,
    Phone,
    Stethoscope,
    LogOut,
    Trash2,
    RefreshCw,
    ArrowLeft,
    Filter,
    Loader2,
    Inbox,
    FileDown,
    X,
    MessageCircle,
    BarChart3,
    Flame,
} from "lucide-react";
import { buildWhatsAppLink } from "../lib/whatsapp";
import AdminStatistics from "./AdminStatistics";
import AdminPromos from "./AdminPromos";
import AdminProcedures from "./AdminProcedures";
import AdminSchedule from "./AdminSchedule";
import { EditContactDialog } from "./EditContact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_clinic-booking-86/artifacts/lfwsi76p_Logo_CE_fondoBlanco.png";

const SPANISH_DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const SPANISH_MONTHS = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function formatDateLong(d) {
    if (!d) return "";
    const day = d.getDate();
    const month = SPANISH_MONTHS[d.getMonth()];
    const weekday = SPANISH_DAYS[d.getDay()];
    const year = d.getFullYear();
    return `${weekday}, ${day} de ${month} de ${year}`;
}
function toISODate(d) {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}
function formatTime12(t) {
    if (!t) return "";
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

export default function AdminDashboard({ user, onLogout }) {
    const [view, setView] = useState("citas"); // 'citas' | 'estadisticas'
    const [filterDate, setFilterDate] = useState(() => new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [exportType, setExportType] = useState("appointments"); // 'appointments' | 'availability'
    const [exportFrom, setExportFrom] = useState(null);
    const [exportTo, setExportTo] = useState(null);
    const [exportPicker, setExportPicker] = useState(null); // 'from' | 'to' | null
    const [exporting, setExporting] = useState(false);
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();

    const loadStats = async () => {
        try {
            const res = await axios.get(`${API}/admin/stats`, {
                withCredentials: true,
            });
            setStats(res.data);
        } catch (err) {
            console.error("loadStats failed:", err);
            /* silent — counters are non-critical */
        }
    };

    const load = async (date) => {
        try {
            const params = date ? { date } : {};
            const res = await axios.get(`${API}/admin/appointments`, {
                params,
                withCredentials: true,
            });
            setAppointments(res.data || []);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                toast.error("Tu sesión expiró. Inicia sesión de nuevo.");
                navigate("/login", { replace: true });
            } else if (status === 403) {
                toast.error("No autorizado.");
                navigate("/login", { replace: true });
            } else {
                toast.error("No se pudieron cargar las reservaciones.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const ctrl = new AbortController();
        const run = async () => {
            try {
                const params = filterDate ? { date: toISODate(filterDate) } : {};
                const res = await axios.get(`${API}/admin/appointments`, {
                    params,
                    withCredentials: true,
                    signal: ctrl.signal,
                });
                setAppointments(res.data || []);
            } catch (err) {
                if (err?.name === "CanceledError") return;
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    toast.error("Tu sesión expiró. Inicia sesión de nuevo.");
                    navigate("/login", { replace: true });
                } else {
                    toast.error("No se pudieron cargar las reservaciones.");
                }
            } finally {
                setLoading(false);
            }
            try {
                const stRes = await axios.get(`${API}/admin/stats`, {
                    withCredentials: true,
                    signal: ctrl.signal,
                });
                setStats(stRes.data);
            } catch (err) {
                if (err?.name === "CanceledError") return;
                console.error("loadStats failed:", err);
            }
        };
        run();
        return () => ctrl.abort();
    }, [filterDate, navigate]);

    const handleLogout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
        } catch (err) {
            console.error("logout failed:", err);
            /* ignore */
        }
        toast.success("Sesión cerrada");
        onLogout?.();
        navigate("/login", { replace: true });
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`¿Cancelar la cita de ${name}? Esta acción no se puede deshacer.`)) {
            return;
        }
        try {
            await axios.delete(`${API}/admin/appointments/${id}`, {
                withCredentials: true,
            });
            toast.success("Cita cancelada");
            setAppointments((prev) => prev.filter((a) => a.id !== id));
            loadStats();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo cancelar la cita");
        }
    };

    const handleUpdateContact = async (id, patient_name, phone, date, time) => {
        await axios.patch(
            `${API}/admin/appointments/${id}`,
            { patient_name, phone, date, time },
            { withCredentials: true }
        );
        await load(filterDate ? toISODate(filterDate) : null);
    };

    const handleExportPdf = async () => {
        if (!exportFrom || !exportTo) {
            toast.error("Selecciona una fecha inicial y una final");
            return;
        }
        const fromIso = toISODate(exportFrom);
        const toIso = toISODate(exportTo);
        if (fromIso > toIso) {
            toast.error("La fecha inicial debe ser menor o igual a la final");
            return;
        }
        setExporting(true);
        const isAvailability = exportType === "availability";
        const endpoint = isAvailability
            ? `${API}/admin/availability.pdf`
            : `${API}/admin/appointments/export.pdf`;
        const fileName = isAvailability
            ? `disponibilidad_${fromIso}_a_${toIso}.pdf`
            : `citas_${fromIso}_a_${toIso}.pdf`;
        try {
            const res = await axios.get(endpoint, {
                params: { from: fromIso, to: toIso },
                responseType: "blob",
                withCredentials: true,
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const objUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objUrl);
            toast.success("PDF descargado");
            setShowExport(false);
        } catch (err) {
            let detail = "No se pudo generar el PDF";
            try {
                const text = await err?.response?.data?.text?.();
                if (text) {
                    const parsed = JSON.parse(text);
                    if (parsed?.detail) detail = parsed.detail;
                }
            } catch (parseErr) {
                console.error("Could not parse PDF error body:", parseErr);
                /* ignore */
            }
            toast.error(detail);
        } finally {
            setExporting(false);
        }
    };

    const grouped = useMemo(() => {
        const map = new Map();
        for (const a of appointments) {
            if (!map.has(a.date)) map.set(a.date, []);
            map.get(a.date).push(a);
        }
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [appointments]);

    const todayISO = toISODate(new Date());

    if (view === "estadisticas") {
        return <AdminStatistics onBack={() => setView("citas")} />;
    }
    if (view === "promos") {
        return <AdminPromos onBack={() => setView("citas")} />;
    }
    if (view === "servicios") {
        return <AdminProcedures onBack={() => setView("citas")} />;
    }
    if (view === "horarios") {
        return <AdminSchedule onBack={() => setView("citas")} />;
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-30 border-b border-foreground/5 glass">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-3 min-w-0">
                        <img
                            src={LOGO_URL}
                            alt="Logo"
                            className="w-10 h-10 rounded-full object-contain bg-white ring-1 ring-border shrink-0"
                        />
                        <div className="leading-tight min-w-0">
                            <p className="font-heading font-medium text-base truncate">
                                Panel administrativo
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                                Ciudad Enfermería
                            </p>
                        </div>
                    </Link>

                    <div className="flex items-center gap-3">
                        <div
                            className="hidden sm:flex items-center gap-2 text-sm"
                            data-testid="admin-user-info"
                        >
                            {user?.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    className="w-8 h-8 rounded-full"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="h-4 w-4 text-primary" />
                                </div>
                            )}
                            <span className="text-muted-foreground truncate max-w-[180px]">
                                {user?.email}
                            </span>
                        </div>
                        <Button
                            variant="outline"
                            onClick={handleLogout}
                            className="rounded-2xl"
                            data-testid="admin-logout-btn"
                        >
                            <LogOut className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Cerrar sesión</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-6">
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                            Reservaciones
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                            {filterDate
                                ? formatDateLong(filterDate)
                                : "Todas las citas"}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {loading
                                ? "Cargando..."
                                : `${appointments.length} ${
                                      appointments.length === 1 ? "cita" : "citas"
                                  }`}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant={filterDate && toISODate(filterDate) === todayISO ? "default" : "outline"}
                            onClick={() => {
                                setFilterDate(new Date());
                                setShowCalendar(false);
                            }}
                            className="rounded-2xl"
                            data-testid="admin-filter-today"
                        >
                            Hoy
                        </Button>
                        <Button
                            variant={showCalendar ? "default" : "outline"}
                            onClick={() => setShowCalendar((v) => !v)}
                            className="rounded-2xl"
                            data-testid="admin-filter-date-toggle"
                        >
                            <Filter className="h-4 w-4 mr-2" />
                            Por fecha
                        </Button>
                        {filterDate && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setFilterDate(null);
                                    setShowCalendar(false);
                                }}
                                className="rounded-2xl"
                                data-testid="admin-filter-clear"
                            >
                                Ver todas
                            </Button>
                        )}
                        <Button
                            variant={showExport ? "default" : "outline"}
                            onClick={() => setShowExport((v) => !v)}
                            className="rounded-2xl"
                            data-testid="admin-export-toggle"
                        >
                            <FileDown className="h-4 w-4 mr-2" />
                            Descargar PDF
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => load(filterDate ? toISODate(filterDate) : null)}
                            className="rounded-2xl"
                            data-testid="admin-refresh-btn"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button
                            variant="default"
                            onClick={() => setView("estadisticas")}
                            className="rounded-2xl"
                            data-testid="admin-view-stats-btn"
                        >
                            <BarChart3 className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Estadísticas</span>
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setView("promos")}
                            className="rounded-2xl border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid="admin-view-promos-btn"
                        >
                            <Flame className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Promociones</span>
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setView("servicios")}
                            className="rounded-2xl"
                            data-testid="admin-view-procedures-btn"
                        >
                            <Stethoscope className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Servicios</span>
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setView("horarios")}
                            className="rounded-2xl"
                            data-testid="admin-view-schedule-btn"
                        >
                            <Clock className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Horarios</span>
                        </Button>
                    </div>
                </div>

                {stats && (
                    <div
                        className="grid grid-cols-3 gap-3 sm:gap-4 mb-8"
                        data-testid="admin-stats"
                    >
                        <Card
                            className="rounded-2xl border-border bg-card p-4 sm:p-5 soft-shadow"
                            data-testid="admin-stat-today"
                        >
                            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                                Hoy
                            </p>
                            <p className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-primary leading-none">
                                {stats.today}
                            </p>
                            <p className="text-[11px] sm:text-xs text-muted-foreground mt-1.5">
                                {stats.today === 1 ? "cita" : "citas"}
                            </p>
                        </Card>
                        <Card
                            className="rounded-2xl border-border bg-card p-4 sm:p-5 soft-shadow"
                            data-testid="admin-stat-week"
                        >
                            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                                Esta semana
                            </p>
                            <p className="font-heading text-2xl sm:text-3xl font-medium tracking-tight leading-none">
                                {stats.week}
                            </p>
                            <p className="text-[11px] sm:text-xs text-muted-foreground mt-1.5">
                                Lun – Dom
                            </p>
                        </Card>
                        <Card
                            className="rounded-2xl border-border bg-card p-4 sm:p-5 soft-shadow"
                            data-testid="admin-stat-upcoming"
                        >
                            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                                Próximas
                            </p>
                            <p className="font-heading text-2xl sm:text-3xl font-medium tracking-tight leading-none">
                                {stats.upcoming}
                            </p>
                            <p className="text-[11px] sm:text-xs text-muted-foreground mt-1.5">
                                hoy en adelante
                            </p>
                        </Card>
                    </div>
                )}

                {showExport && (
                    <Card
                        className="rounded-2xl border-border bg-card p-5 sm:p-6 mb-8 soft-shadow"
                        data-testid="admin-export-panel"
                    >
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
                                    Exportar a PDF
                                </p>
                                <p className="font-heading text-lg">
                                    Selecciona tipo y rango de fechas
                                </p>
                            </div>
                            <button
                                onClick={() => setShowExport(false)}
                                className="text-muted-foreground hover:text-foreground p-1"
                                aria-label="Cerrar"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-2 mb-5">
                            <button
                                type="button"
                                onClick={() => setExportType("appointments")}
                                className={`text-left rounded-2xl border p-4 transition-all ${
                                    exportType === "appointments"
                                        ? "bg-primary/5 border-primary ring-2 ring-primary"
                                        : "bg-background border-border hover:border-primary/50"
                                }`}
                                data-testid="admin-export-type-appointments"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <FileDown className="h-4 w-4 text-primary" />
                                    <span className="font-medium text-sm">Citas agendadas</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Detalle de pacientes con cita en el rango.
                                </p>
                            </button>
                            <button
                                type="button"
                                onClick={() => setExportType("availability")}
                                className={`text-left rounded-2xl border p-4 transition-all ${
                                    exportType === "availability"
                                        ? "bg-primary/5 border-primary ring-2 ring-primary"
                                        : "bg-background border-border hover:border-primary/50"
                                }`}
                                data-testid="admin-export-type-availability"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Clock className="h-4 w-4 text-primary" />
                                    <span className="font-medium text-sm">Horarios disponibles</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Slots libres vs ocupados, día por día.
                                </p>
                            </button>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 mb-4">
                            <div>
                                <p className="text-xs text-muted-foreground mb-1.5">
                                    Fecha inicial
                                </p>
                                <Button
                                    variant="outline"
                                    onClick={() => setExportPicker(exportPicker === "from" ? null : "from")}
                                    className="w-full rounded-2xl justify-start font-normal"
                                    data-testid="admin-export-from-btn"
                                >
                                    <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
                                    {exportFrom ? formatDateLong(exportFrom) : "Seleccionar fecha"}
                                </Button>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1.5">
                                    Fecha final
                                </p>
                                <Button
                                    variant="outline"
                                    onClick={() => setExportPicker(exportPicker === "to" ? null : "to")}
                                    className="w-full rounded-2xl justify-start font-normal"
                                    data-testid="admin-export-to-btn"
                                >
                                    <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
                                    {exportTo ? formatDateLong(exportTo) : "Seleccionar fecha"}
                                </Button>
                            </div>
                        </div>

                        {exportPicker && (
                            <div className="flex justify-center mb-4 p-2 rounded-xl bg-muted/40">
                                <Calendar
                                    mode="single"
                                    selected={exportPicker === "from" ? exportFrom : exportTo}
                                    onSelect={(d) => {
                                        if (!d) return;
                                        if (exportPicker === "from") {
                                            setExportFrom(d);
                                            // If "to" is empty or before "from", set "to" too
                                            if (!exportTo || exportTo < d) setExportTo(d);
                                            setExportPicker("to");
                                        } else {
                                            setExportTo(d);
                                            setExportPicker(null);
                                        }
                                    }}
                                    weekStartsOn={1}
                                />
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 mb-4">
                            <button
                                type="button"
                                onClick={() => {
                                    const t = new Date();
                                    setExportFrom(t);
                                    setExportTo(t);
                                    setExportPicker(null);
                                }}
                                className="text-xs rounded-full px-3 py-1.5 bg-muted hover:bg-secondary/40 transition-colors"
                                data-testid="admin-export-preset-today"
                            >
                                Hoy
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const t = new Date();
                                    const start = new Date(t);
                                    start.setDate(t.getDate() - t.getDay() + 1); // Mon
                                    const end = new Date(start);
                                    end.setDate(start.getDate() + 6); // Sun
                                    setExportFrom(start);
                                    setExportTo(end);
                                    setExportPicker(null);
                                }}
                                className="text-xs rounded-full px-3 py-1.5 bg-muted hover:bg-secondary/40 transition-colors"
                                data-testid="admin-export-preset-week"
                            >
                                Esta semana
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const t = new Date();
                                    const start = new Date(t.getFullYear(), t.getMonth(), 1);
                                    const end = new Date(t.getFullYear(), t.getMonth() + 1, 0);
                                    setExportFrom(start);
                                    setExportTo(end);
                                    setExportPicker(null);
                                }}
                                className="text-xs rounded-full px-3 py-1.5 bg-muted hover:bg-secondary/40 transition-colors"
                                data-testid="admin-export-preset-month"
                            >
                                Este mes
                            </button>
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowExport(false)}
                                className="rounded-2xl"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleExportPdf}
                                disabled={!exportFrom || !exportTo || exporting}
                                className="rounded-2xl"
                                data-testid="admin-export-download-btn"
                            >
                                {exporting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Generando...
                                    </>
                                ) : (
                                    <>
                                        <FileDown className="h-4 w-4 mr-2" />
                                        Descargar PDF
                                    </>
                                )}
                            </Button>
                        </div>
                    </Card>
                )}

                {showCalendar && (
                    <Card className="rounded-2xl border-border bg-card p-4 mb-8 inline-block">
                        <Calendar
                            mode="single"
                            selected={filterDate}
                            onSelect={(d) => {
                                if (d) {
                                    setFilterDate(d);
                                    setShowCalendar(false);
                                }
                            }}
                            weekStartsOn={1}
                        />
                    </Card>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Cargando reservaciones...
                    </div>
                ) : appointments.length === 0 ? (
                    <Card
                        className="rounded-3xl border-border bg-card p-12 text-center soft-shadow"
                        data-testid="admin-empty-state"
                    >
                        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                            <Inbox className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <p className="font-heading text-xl mb-2">Sin reservaciones</p>
                        <p className="text-muted-foreground">
                            {filterDate
                                ? "No hay citas agendadas para esta fecha."
                                : "Aún no se han agendado citas."}
                        </p>
                    </Card>
                ) : (
                    <div className="space-y-8" data-testid="admin-appointments-list">
                        {grouped.map(([date, list]) => (
                            <section key={date}>
                                <div className="flex items-center gap-2 mb-3">
                                    <CalendarDays className="h-4 w-4 text-primary" />
                                    <h2 className="font-heading text-lg">
                                        {formatDateLong(new Date(`${date}T00:00:00`))}
                                    </h2>
                                    <span className="text-xs text-muted-foreground">
                                        · {list.length} {list.length === 1 ? "cita" : "citas"}
                                    </span>
                                </div>

                                <Card className="rounded-3xl border-border bg-card overflow-hidden soft-shadow">
                                    <div className="hidden md:grid md:grid-cols-[110px_1fr_180px_220px_60px] px-6 py-3 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                                        <div>Hora</div>
                                        <div>Paciente</div>
                                        <div>Procedimiento</div>
                                        <div>Teléfono / Folio</div>
                                        <div></div>
                                    </div>

                                    {list.map((a) => (
                                        <div
                                            key={a.id}
                                            className="grid md:grid-cols-[110px_1fr_180px_220px_60px] gap-2 md:gap-0 px-5 sm:px-6 py-4 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                                            data-testid={`admin-appt-${a.id}`}
                                        >
                                            <div className="flex items-center gap-2 font-heading text-base text-primary">
                                                <Clock className="h-4 w-4 md:hidden" />
                                                {formatTime12(a.time)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium truncate flex items-center gap-2">
                                                    <User className="h-4 w-4 md:hidden text-muted-foreground" />
                                                    {a.patient_name}
                                                    {a.source === "meta_ads" && (
                                                        <span
                                                            className="shrink-0 inline-flex items-center rounded-full bg-[#1877F2]/10 text-[#1877F2] text-[10px] font-semibold px-2 py-0.5"
                                                            title={a.utm_campaign ? `Campaña: ${a.utm_campaign}` : "Llegó desde Meta Ads"}
                                                            data-testid={`admin-ads-badge-${a.id}`}
                                                        >
                                                            📣 Ads
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <Stethoscope className="h-3.5 w-3.5 text-muted-foreground md:hidden" />
                                                <span className="truncate">{a.procedure_name}</span>
                                            </div>
                                            <div className="text-sm text-muted-foreground space-y-0.5 min-w-0">
                                                {a.phone ? (
                                                    <p className="flex items-center gap-1.5 truncate">
                                                        <Phone className="h-3.5 w-3.5 shrink-0" />
                                                        <a
                                                            href={`tel:${a.phone.replace(/\s/g, "")}`}
                                                            className="hover:text-foreground truncate"
                                                        >
                                                            {a.phone}
                                                        </a>
                                                    </p>
                                                ) : (
                                                    <p className="text-xs italic">Sin teléfono</p>
                                                )}
                                                <p className="text-xs font-mono opacity-70 truncate">
                                                    {a.id.slice(0, 8)}
                                                </p>
                                            </div>
                                            <div className="flex md:justify-end gap-1">
                                                <EditContactDialog
                                                    appt={a}
                                                    testIdPrefix="admin-edit"
                                                    onSave={(n, p, d, t) => handleUpdateContact(a.id, n, p, d, t)}
                                                />
                                                {a.phone && buildWhatsAppLink(a) && (
                                                    <a
                                                        href={buildWhatsAppLink(a)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-muted-foreground hover:text-[#25D366] transition-colors p-2 rounded-lg hover:bg-[#25D366]/10"
                                                        title="Recordar/confirmar por WhatsApp"
                                                        data-testid={`admin-whatsapp-${a.id}`}
                                                    >
                                                        <MessageCircle className="h-4 w-4" />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(a.id, a.patient_name)}
                                                    className="text-muted-foreground hover:text-destructive transition-colors p-2 rounded-lg hover:bg-destructive/10"
                                                    title="Cancelar cita"
                                                    data-testid={`admin-delete-${a.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </Card>
                            </section>
                        ))}
                    </div>
                )}

                <div className="mt-12">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Volver al sitio público
                    </Link>
                </div>
            </main>
        </div>
    );
}
